/**
 * API calls of the billing tab and the voucher page (work order item 7), on the shared
 * {@link opsClient}. Per-shop reads and the trial extension carry the shop grant; the voucher
 * calls are cross-shop and need none.
 */

import type {
  CreateVoucherRequestDto,
  OpsBillingDto,
  OpsVoucherListDto,
  OpsVoucherUsageListDto,
  OpsVoucherWriteResultDto,
  TrialExtensionResultDto,
} from '../../../../libs/api-client/src/generated/models';
import { opsClient, shopGrant } from './ops-api';
import { parseSyncOutcome, type SubscriptionSyncOutcome } from './ops-billing';

// Re-exported from the generated client rather than restated here — see `support-api.ts`.
export type {
  OpsBillingActionDto as OpsBillingAction,
  OpsBillingCycleDto as OpsBillingCycle,
  OpsBillingDto as OpsBilling,
  OpsBillingSubscriptionDto as OpsBillingSubscription,
  OpsFailedUsageRecordDto as OpsFailedUsageRecord,
  OpsShopVoucherUsageDto as OpsShopVoucherUsage,
  OpsSubscriptionTransitionDto as OpsSubscriptionTransition,
  OpsVoucherDto as OpsVoucher,
  OpsVoucherListDto as OpsVoucherList,
  OpsVoucherUsageDto as OpsVoucherUsage,
  OpsVoucherUsageListDto as OpsVoucherUsageList,
  ShopifyUserErrorDto as ShopifyUserError,
  TrialExtensionResultDto as TrialExtensionResult,
} from '../../../../libs/api-client/src/generated/models';

/** One shop's billing detail. */
export async function fetchShopBilling(shopId: string): Promise<OpsBillingDto> {
  const { data } = await opsClient().getOpsShopBilling({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
  });
  return data;
}

/** Extends the shop's running trial through Shopify. A Shopify refusal returns `extended: false`. */
export async function extendShopTrial(
  shopId: string,
  input: { days: number; reason: string },
): Promise<TrialExtensionResultDto> {
  const { data } = await opsClient().extendOpsShopTrial({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
    trialExtensionRequestDto: input,
  });
  return data;
}

/**
 * Re-reads the shop's subscription from Shopify and replays it onto the local row (audited).
 * Carries the shop's access grant like every other call of the shop file.
 */
export async function syncShopSubscription(
  shopId: string,
  reason: string,
): Promise<SubscriptionSyncOutcome> {
  const response =
    await opsClient().internalAdminControllerSyncSubscriptionFromShopifyV1({
      shopId,
      xOpsShopAccess: shopGrant(shopId),
      operatorReasonDto: { reason },
    });
  return parseSyncOutcome(response.data);
}

/** Every voucher code. */
export async function fetchVouchers(): Promise<OpsVoucherListDto> {
  const { data } = await opsClient().listOpsVouchers();
  return data;
}

/** Creates a voucher code (audited). */
export async function createVoucher(
  input: CreateVoucherRequestDto,
): Promise<OpsVoucherWriteResultDto> {
  const { data } = await opsClient().createOpsVoucher({
    createVoucherRequestDto: input,
  });
  return data;
}

/** Deactivates a voucher code (audited). There is no delete. */
export async function deactivateVoucher(
  id: string,
  reason: string,
): Promise<OpsVoucherWriteResultDto> {
  const { data } = await opsClient().deactivateOpsVoucher({
    id,
    deactivateVoucherRequestDto: { reason },
  });
  return data;
}

/** Who redeemed a voucher code. */
export async function fetchVoucherUsages(
  id: string,
): Promise<OpsVoucherUsageListDto> {
  const { data } = await opsClient().listOpsVoucherUsages({ id });
  return data;
}
